import { create } from 'zustand'
import type { FirstVisitNotice } from './locate'
import { initNav, navState, popOverlay, pushOverlay, replaceOverlay } from './router'

export type Metric =
  | 'conditions'
  | 'precipitation'
  | 'wind'
  | 'feels'
  | 'humidity'
  | 'visibility'
  | 'pressure'
  | 'uv'
  | 'aqi'
  | 'averages'
  | 'sun'
  | 'moon'
  | 'tides'

export const METRIC_IDS: readonly Metric[] = [
  'conditions',
  'precipitation',
  'wind',
  'feels',
  'humidity',
  'visibility',
  'pressure',
  'uv',
  'aqi',
  'averages',
  'sun',
  'moon',
  'tides',
]

export type Sheet =
  | { kind: 'detail'; metric: Metric; day: number; origin: string }
  | { kind: 'sources'; origin: string }
  | { kind: 'settings'; origin: string }
  | { kind: 'alert'; alertId: string; origin: string }
  | { kind: 'timemachine'; tab: TimeMachineTab; origin: string }

export type TimeMachineTab = 'past' | 'ahead'

/** What a history entry remembers about the overlays on the view. */
export interface Overlay {
  sheet: Sheet | null
  list: boolean
}

// ─── URL form of an overlay: `#wind/2`, `#sources`, `#timemachine/ahead`, `#places` ─────

export function overlayHash(o: Overlay): string | null {
  const s = o.sheet
  if (!s) return o.list ? 'places' : null
  switch (s.kind) {
    case 'detail':
      return s.day > 0 ? `${s.metric}/${s.day}` : s.metric
    case 'timemachine':
      return s.tab === 'ahead' ? 'timemachine/ahead' : 'timemachine'
    case 'alert':
      return `alert/${encodeURIComponent(s.alertId)}`
    default:
      return s.kind
  }
}

export function parseOverlayHash(hash: string): Overlay | null {
  let text = hash.replace(/^#/, '')
  try {
    text = decodeURIComponent(text)
  } catch {
    return null
  }
  const [head, arg] = text.split(/\/(.*)/s)
  if (!head) return null
  if (head === 'places') return { sheet: null, list: true }
  if (head === 'sources' || head === 'settings') return { sheet: { kind: head, origin: '' }, list: false }
  if (head === 'timemachine') return { sheet: { kind: 'timemachine', tab: arg === 'ahead' ? 'ahead' : 'past', origin: '' }, list: false }
  if (head === 'alert') return { sheet: { kind: 'alert', alertId: arg ?? '', origin: '' }, list: false }
  if ((METRIC_IDS as readonly string[]).includes(head)) {
    const day = Math.max(0, Math.min(15, Math.floor(Number(arg) || 0)))
    return { sheet: { kind: 'detail', metric: head as Metric, day, origin: '' }, list: false }
  }
  return null
}

/** The overlay of the current history entry: what we stored there, else what the hash says. */
function readOverlay(): Overlay {
  const stored = navState()?.overlay as Overlay | null | undefined
  if (stored && typeof stored === 'object' && 'list' in stored) {
    // Restored overlays never morph: the card they came from may be scrolled away or gone.
    return { sheet: stored.sheet ? { ...stored.sheet, origin: '' } : null, list: !!stored.list }
  }
  return parseOverlayHash(typeof location !== 'undefined' ? location.hash : '') ?? { sheet: null, list: false }
}

const withoutOrigin = (s: Sheet | null): Sheet | null => (s ? { ...s, origin: '' } : null)

interface UiState {
  sheet: Sheet | null
  /** Element that opened the sheet, for focus restore. */
  returnFocus: HTMLElement | null
  phoneList: boolean
  /** Opens a sheet as a new history entry (Back closes it). */
  open: (sheet: Sheet) => void
  /** Swaps the open sheet for another in place (same entry): one close still returns to the forecast. */
  swap: (sheet: Sheet) => void
  /** Closes the top overlay (one step back). */
  close: () => void
  /** A sheet's own view changed (metric / day / tab): kept in the URL, no new entry. */
  updateSheet: (sheet: Sheet) => void
  setPhoneList: (open: boolean) => void
  /** First visit outside coverage / without a location: an honest note on the forecast. */
  notice: FirstVisitNotice
  setNotice: (n: FirstVisitNotice) => void
  /** When search was last asked for focus (the `/` and ⌘K shortcuts; searches take it once). */
  searchFocusAt: number
  focusSearch: () => void
  /** Places list edit mode: remove and drag handles on every row (touch-friendly). */
  editingPlaces: boolean
  setEditingPlaces: (on: boolean) => void
  /** Browser geolocation in progress, and its last error (shown once, as a toast). */
  locating: boolean
  locateError: string | null
  setLocate: (busy: boolean, error: string | null) => void
}

const active = () => (typeof document !== 'undefined' ? (document.activeElement as HTMLElement | null) : null)

export const useUi = create<UiState>()((set, get) => ({
  sheet: null,
  returnFocus: null,
  phoneList: false,
  open: (sheet) => {
    if (get().sheet) return get().swap(sheet)
    set({ sheet, returnFocus: active() })
    const next: Overlay = { sheet: withoutOrigin(sheet), list: get().phoneList }
    pushOverlay(overlayHash(next) ?? '', next)
  },
  swap: (sheet) => {
    // Focus still returns to whatever opened the first sheet.
    set({ sheet })
    get().updateSheet(sheet)
  },
  close: () => {
    if (!get().sheet) return
    if (!popOverlay()) set({ sheet: null })
  },
  updateSheet: (sheet) => {
    const next: Overlay = { sheet: withoutOrigin(sheet), list: get().phoneList }
    replaceOverlay(overlayHash(next), next)
  },
  setPhoneList: (open) => {
    if (open === get().phoneList) return
    if (open) {
      set({ phoneList: true, returnFocus: active() })
      pushOverlay('places', { sheet: null, list: true } satisfies Overlay)
    } else if (!popOverlay()) set({ phoneList: false })
  },
  notice: null,
  setNotice: (notice) => set({ notice }),
  searchFocusAt: 0,
  focusSearch: () => set({ searchFocusAt: Date.now() }),
  editingPlaces: false,
  setEditingPlaces: (editingPlaces) => set({ editingPlaces }),
  locating: false,
  locateError: null,
  setLocate: (locating, locateError) => set({ locating, locateError }),
}))

/** Search: the places page (the v4 palette replaces it), over every layout. */
export function openSearch(): void {
  const ui = useUi.getState()
  if (!ui.phoneList) ui.setPhoneList(true)
  ui.focusSearch()
}

/** Back / Forward: show exactly the overlays of the entry we arrived at. */
function syncFromHistory() {
  const o = readOverlay()
  const cur = useUi.getState()
  const opening = (!!o.sheet && !cur.sheet) || (o.list && !cur.phoneList)
  useUi.setState({
    sheet: o.sheet,
    phoneList: o.list,
    ...(opening ? { returnFocus: active() } : {}),
  })
}

if (typeof window !== 'undefined') window.addEventListener('popstate', syncFromHistory)

/**
 * On load (main.tsx): a `#hash` overlay — a shared or reloaded sheet link — opens over its
 * view, with the view synthesized underneath so closing it (or Back) returns to the forecast
 * instead of leaving the app. A reload keeps the entries it already had.
 */
export function initOverlays(desktop: boolean): void {
  if (typeof window === 'undefined') return
  const fromEntry = navState()
  const o = readOverlay()
  if (o.list && desktop) o.list = false // the desktop sidebar is always on screen
  if (!o.sheet && !o.list) {
    if (location.hash && parseOverlayHash(location.hash)) replaceOverlay(null, null)
    initNav()
    return
  }
  if (!fromEntry) {
    history.replaceState({ wf: { depth: 0, back: null, overlay: null } }, '', location.pathname + location.search)
    pushOverlay(overlayHash(o) ?? '', o)
  }
  useUi.setState({ sheet: o.sheet, phoneList: o.list })
}
